/**
 * AccessControlHubPage.js — Unified user / RBAC administration surface.
 *
 * Phase 1 of the Administration-section access consolidation. Collapses
 * four legacy sidebar entries into one tabbed page:
 *
 *   • User Management    → user accounts, activation, profile (users:read)
 *   • Role Management    → role definitions, role assignment   (users:read)
 *   • Permission Manager → per-user overrides + role-permissions matrix (users:update)
 *   • Sidebar Visibility → which sidebar items each role sees   (users:read)
 *
 * These four pages are the security/admin equivalent of a single workflow
 * ("I just hired someone — create an account, assign a role, override one
 * permission, decide what sidebar entries they see"). The four-way split
 * forced sysadmins to bounce between pages constantly during onboarding.
 * One tabbed surface keeps the whole flow in one place.
 *
 * Kept separate from PersonnelHubPage on purpose: HR records and security
 * policy are different concerns even when administered by overlapping
 * people. Mixing them would mean a role with personnel:read but not
 * users:update would see an inconsistent set of tabs.
 *
 * RBAC note: the four tabs span TWO permission resources (users:read for
 * three of them, users:update for Permission Manager). Sidebar entry uses
 * the any-of array shape so users with either permission see "Access".
 * Inner tabs hide themselves based on the narrower permission.
 */
import React, { useState } from 'react';
import { Users, Shield, Lock, Eye } from 'lucide-react';
import UserManagementPage from './UserManagementPage';
import RoleManagementPage from './RoleManagementPage';
import PermissionManagerPage from './PermissionManagerPage';
import { useNavStep } from '../lib/NavHistory';
import SidebarVisibilityPage from './SidebarVisibilityPage';

const TABS = [
  { id: 'users',       label: 'Users',              icon: Users,  perm: 'users:read'   },
  { id: 'roles',       label: 'Roles',              icon: Shield, perm: 'users:read'   },
  { id: 'permissions', label: 'Permissions',        icon: Lock,   perm: 'users:update' },
  { id: 'visibility',  label: 'Sidebar Visibility', icon: Eye,    perm: 'users:read'   },
];

export default function AccessControlHubPage({ user, onNavigate, initialTab }) {
  const perms = Array.isArray(user?.permissions) ? user.permissions : [];
  const visibleTabs = TABS.filter((t) => perms.includes(t.perm));

  const defaultTab = visibleTabs.find((t) => t.id === initialTab)?.id
    || visibleTabs[0]?.id
    || 'users';

  const [activeTab, setActiveTab] = useState(defaultTab);

  // #65 Phase B: being on a non-default tab is one Back step; Back returns to
  // the default (Users) tab. Called unconditionally, above the early return.
  // Label is the generic tab name (no PHI).
  const activeTabLabel = visibleTabs.find((t) => t.id === activeTab)?.label || 'Access Control';
  useNavStep({
    active: activeTab !== defaultTab,
    label: `${activeTabLabel} tab`,
    onBack: () => setActiveTab(defaultTab),
  });

  if (visibleTabs.length === 0) {
    return (
      <div className="page">
        <h1 className="page-title">Access Control</h1>
        <div className="empty-state" style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>
          <Lock size={48} />
          <p>You don't have permission to view access control.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page access-hub-page">
      <div className="access-hub-header">
        <h1 className="page-title">Access Control</h1>
      </div>

      <div className="access-hub-tabs" role="tablist" aria-label="Access control sections">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`access-hub-panel-${tab.id}`}
              className={`access-hub-tab ${isActive ? 'access-hub-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <Icon size={18} strokeWidth={isActive ? 2.2 : 1.8} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div
        className="access-hub-panel"
        id={`access-hub-panel-${activeTab}`}
        role="tabpanel"
      >
        {activeTab === 'users' && (
          <div className="access-hub-embedded">
            <UserManagementPage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'roles' && (
          <div className="access-hub-embedded">
            <RoleManagementPage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'permissions' && (
          <div className="access-hub-embedded">
            <PermissionManagerPage user={user} onNavigate={onNavigate} />
          </div>
        )}
        {activeTab === 'visibility' && (
          <div className="access-hub-embedded">
            <SidebarVisibilityPage user={user} onNavigate={onNavigate} />
          </div>
        )}
      </div>

      <style jsx>{`
        .access-hub-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }
        .access-hub-tabs {
          display: flex;
          gap: 4px;
          border-bottom: 1px solid #E8ECF1;
          margin-bottom: 16px;
          overflow-x: auto;
        }
        .access-hub-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border: none;
          background: transparent;
          color: #6C757D;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          white-space: nowrap;
          transition: color 0.15s, border-color 0.15s;
        }
        .access-hub-tab:hover { color: #2E8B98; }
        .access-hub-tab--active {
          color: #2E8B98;
          border-bottom-color: #2E8B98;
          font-weight: 600;
        }
        .access-hub-embedded :global(.page) {
          padding: 0;
          background: transparent;
        }
        .access-hub-embedded :global(.page-title) {
          display: none;
        }
      `}</style>
    </div>
  );
}
